import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Activity, Package, Clock, Filter } from 'lucide-react';

import { API } from '@/config';

export default function TransactionsPage() {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchTransactions();
  }, [filter]);

  const fetchTransactions = async () => {
    try {
      const url = filter === 'all'
        ? `${API}/warehouse/transactions?limit=100`
        : `${API}/warehouse/transactions?transaction_type=${filter}&limit=100`;
      const response = await axios.get(url);
      setTransactions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setLoading(false);
    }
  };

  const getTransactionBadge = (type) => {
    const styles = {
      receipt: 'bg-green-100 text-green-700',
      pick: 'bg-purple-100 text-purple-700',
      putaway: 'bg-blue-100 text-blue-700',
      shipment: 'bg-orange-100 text-orange-700',
      adjustment: 'bg-yellow-100 text-yellow-700',
      transfer: 'bg-indigo-100 text-indigo-700'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${styles[type] || 'bg-gray-100 text-gray-700'}`}>
        {type.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/warehouse')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Activity className="w-8 h-8" />
              Warehouse Transactions
            </h1>
            <p className="text-gray-600 mt-1">Complete movement history</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold">Filter by Type</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('receipt')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'receipt' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Receipts
          </button>
          <button
            onClick={() => setFilter('putaway')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'putaway' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Putaways
          </button>
          <button
            onClick={() => setFilter('pick')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'pick' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Picks
          </button>
          <button
            onClick={() => setFilter('shipment')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'shipment' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Shipments
          </button>
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading transactions...</div>
        ) : transactions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lot Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    From Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    To Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((txn, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm">
                        <Clock className="w-4 h-4 text-gray-400" />
                        {new Date(txn.timestamp).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getTransactionBadge(txn.transaction_type)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-gray-400" />
                        {txn.item_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                      {txn.lot_number || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {txn.quantity.toLocaleString()} {txn.uom}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {txn.from_location_id || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {txn.to_location_id || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{txn.user}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center text-gray-500">
            <Activity className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">No Transactions Found</p>
            <p className="text-sm">No transactions matching the selected filter</p>
          </div>
        )}
      </div>
    </div>
  );
}
